import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";

export default function CategoriesSection({ isAdmin, onChanged }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch (err) { console.error("Failed to load categories:", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = () => { setEditing(null); setForm({ name: "", description: "" }); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description || "" }); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/categories", form);
        toast.success("Category added");
      }
      setDialogOpen(false); fetchCategories(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Qualifications using this category must be reassigned first.`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Category deleted"); fetchCategories(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="categories-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-base flex items-center gap-2"><Tag size={16} /> Qualification Categories</h3>
          <p className="text-xs text-muted-foreground mt-1">Manage the categories used to group qualifications and colour-code the calendar.</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-category-btn">
            <Plus size={14} className="mr-1" /> Add Category
          </Button>
        )}
      </div>

      <div className="border border-border rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left">
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Name</th>
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs hidden md:table-cell">Description</th>
            {isAdmin && <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs w-24">Actions</th>}
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : categories.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">No categories yet. Add one to get started.</td></tr>
            ) : categories.map(c => (
              <tr key={c.id} className="border-b border-border hover:bg-slate-50/50" data-testid={`category-row-${c.id}`}>
                <td className="px-4 py-2.5 font-medium">{c.name}</td>
                <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell">{c.description || "—"}</td>
                {isAdmin && (
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`edit-category-${c.id}`}><Pencil size={14} /></button>
                      <button onClick={() => remove(c)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-category-${c.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="category-name-input" />
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="category-description-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="category-submit-btn">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
